/**
 * Notion-style archives (P-523, P-524, P-529): a page is `Title <id>.md`, its subpages live in the
 * folder `Title <id>/`, databases are `Title <id>.csv` with their row pages in `Title <id>/`.
 */
import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import { databaseCsv, importCsv } from './csv';
import type { DatabaseModel } from './database';
import { htmlToBlocks, pageToHtml } from './html';
import { blocksToMarkdown, markdownTitle, markdownToBlocks, type MdBlock, type MdInline } from './markdown';
import type { PageParent } from './types';
import type { Workspace } from './workspace';

export type ExportFormat = 'markdown' | 'html';

export interface ExportOptions {
  format: ExportFormat;
  includeSubpages: boolean;
  /** The user exporting (for "me" in database filters). */
  me: string;
  /** The page body as blocks. */
  blocksOf(pageId: string): Promise<MdBlock[]>;
}

function safeName(title: string): string {
  return (title.trim() || 'Untitled').replace(/[\\/:*?"<>|#%]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);
}

function baseName(ws: Workspace, id: string): string {
  return `${safeName(ws.getPage(id)?.title ?? '')} ${id.slice(0, 32)}`;
}

function encodePath(p: string): string {
  return p.split('/').map(encodeURIComponent).join('/');
}

/**
 * Collects the files of an export: the page (and with `includeSubpages`, its subpages and the
 * databases in it, recursively). Paths are relative to the archive root.
 */
export async function exportFiles(ws: Workspace, db: DatabaseModel, rootIds: string[], opts: ExportOptions): Promise<Map<string, string>> {
  const files = new Map<string, string>();
  const ext = opts.format === 'html' ? 'html' : 'md';

  const visit = async (id: string, dir: string) => {
    const page = ws.getPage(id);
    if (!page || page.trashed) return;
    const name = baseName(ws, id);
    const folder = `${dir}${name}/`;
    if (page.kind === 'database' && db.isDatabase(id)) {
      files.set(`${dir}${name}.csv`, databaseCsv(db, id, opts.me));
      if (opts.includeSubpages) for (const r of db.rows(id)) await visit(r.id, folder);
      return;
    }
    const blocks = await opts.blocksOf(id);
    const children = ws.childIds(id);
    const linkFor = (target: string, kind: 'page' | 'database') => {
      const p = ws.getPage(target);
      if (!p) return null;
      const inside = children.includes(target) && opts.includeSubpages;
      const fileExt = kind === 'database' ? 'csv' : ext;
      const href = inside ? `${encodePath(name)}/${encodePath(baseName(ws, target))}.${fileExt}` : `${encodePath(baseName(ws, target))}.${fileExt}`;
      return { title: p.title || 'Untitled', href };
    };
    const linkOpts = { pageLink: (t: string) => linkFor(t, ws.getPage(t)?.kind === 'database' ? 'database' : 'page'), databaseLink: (t: string) => linkFor(t, 'database') };
    // Subpages that no block shows (created from the sidebar) are listed at the end, as in Notion.
    const shown = new Set<string>();
    const walk = (bs: MdBlock[]) => bs.forEach((b) => {
      if (b.type === 'page' || b.type === 'linkToPage') shown.add(String(b.props?.pageId ?? ''));
      if (b.type === 'database') shown.add(String(b.props?.dbId ?? ''));
      walk(b.children ?? []);
    });
    walk(blocks);
    const all = [...blocks];
    if (opts.includeSubpages) for (const c of children) if (!shown.has(c)) all.push(ws.getPage(c)?.kind === 'database' ? { type: 'database', props: { dbId: c } } : { type: 'page', props: { pageId: c } });
    const title = page.title || 'Untitled';
    files.set(`${dir}${name}.${ext}`, opts.format === 'html' ? pageToHtml(title, all, linkOpts) : `# ${title}\n\n${blocksToMarkdown(all, linkOpts)}`);
    if (opts.includeSubpages) for (const c of children) await visit(c, folder);
  };

  for (const id of rootIds) await visit(id, '');
  return files;
}

export function zipFiles(files: Map<string, string | Uint8Array>): Uint8Array {
  const entries: Record<string, Uint8Array> = {};
  for (const [path, content] of files) entries[path] = typeof content === 'string' ? strToU8(content) : content;
  return zipSync(entries, { level: 6 });
}

export function unzipFiles(data: Uint8Array): Map<string, string> {
  const out = new Map<string, string>();
  for (const [path, bytes] of Object.entries(unzipSync(data))) {
    if (path.endsWith('/') || path.startsWith('__MACOSX/')) continue;
    out.set(path, strFromU8(bytes));
  }
  return out;
}

export interface ImportTarget {
  ws: Workspace;
  db: DatabaseModel;
  /** Writes a page body. */
  setBody(pageId: string, blocks: MdBlock[]): void;
}

const ID_SUFFIX = /\s+([0-9a-f]{32}|[0-9a-f-]{36})$/i;

function titleFromFile(name: string): string {
  return decodeURIComponent(name).replace(/\.(md|markdown|csv|html?)$/i, '').replace(ID_SUFFIX, '').trim();
}

/**
 * Imports a Notion-style tree of Markdown, HTML and CSV files (P-523): every `.md`/`.html` becomes a
 * page, every `.csv` a database, a folder named like a file holds that file's subpages, and links
 * between the files become page and database blocks. Returns the ids of the top-level pages.
 */
export function importFiles(target: ImportTarget, files: Map<string, string>, parent: PageParent): string[] {
  const { ws, db } = target;
  const paths = [...files.keys()].filter((p) => /\.(md|markdown|csv|html?)$/i.test(p)).sort();
  const created = new Map<string, string>();
  const stem = (p: string) => p.replace(/\.(md|markdown|csv|html?)$/i, '');
  const parentOf = (p: string): string | null => {
    const dir = p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '';
    if (!dir) return null;
    return paths.find((x) => stem(x) === dir) ?? null;
  };
  // Parents first (shorter paths), so children can be attached.
  const ordered = [...paths].sort((a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b));
  const roots: string[] = [];
  const bodies: Array<{ id: string; path: string; md: string; html: boolean }> = [];
  for (const path of ordered) {
    const parentPath = parentOf(path);
    const parentId = parentPath ? created.get(parentPath) : undefined;
    const fileName = path.slice(path.lastIndexOf('/') + 1);
    const where: PageParent = parentId ? (ws.getPage(parentId)?.kind === 'database' ? { type: 'database', id: parentId } : { type: 'page', id: parentId }) : parent;
    const content = files.get(path)!;
    let id: string;
    if (/\.csv$/i.test(path)) {
      id = importCsv(db, where, titleFromFile(fileName), content);
    } else if (where.type === 'database') {
      // Row pages: their CSV row already exists; attach the body to the row with that title.
      const title = titleFromFile(fileName);
      const row = db.rows(where.id).find((r) => r.title === title && !bodies.some((b) => b.id === r.id));
      id = row?.id ?? db.addRow(where.id, { title });
    } else {
      const isHtml = /\.html?$/i.test(path);
      const mdTitle = isHtml ? htmlToBlocks(content).title : markdownTitle(content).title;
      id = ws.createPage({ parent: where, title: mdTitle ?? titleFromFile(fileName) });
    }
    created.set(path, id);
    if (!parentId) roots.push(id);
    if (!/\.csv$/i.test(path)) bodies.push({ id, path, md: content, html: /\.html?$/i.test(path) });
  }

  // Bodies, with links to other imported files turned into page and database blocks.
  const resolve = (from: string, href: string): string | null => {
    if (/^[a-z]+:/i.test(href)) return null;
    const dir = from.includes('/') ? from.slice(0, from.lastIndexOf('/') + 1) : '';
    let rel: string;
    try {
      rel = decodeURIComponent(href);
    } catch {
      rel = href;
    }
    const parts = (dir + rel).split('/');
    const norm: string[] = [];
    for (const p of parts) {
      if (p === '..') norm.pop();
      else if (p !== '.') norm.push(p);
    }
    return created.get(norm.join('/')) ?? null;
  };
  for (const b of bodies) {
    let blocks: MdBlock[];
    if (b.html) blocks = htmlToBlocks(b.md).blocks;
    else blocks = markdownToBlocks(markdownTitle(b.md).body);
    const linkify = (bs: MdBlock[]): MdBlock[] =>
      bs.map((x) => {
        const content = Array.isArray(x.content) ? (x.content as MdInline[]) : null;
        if (x.type === 'paragraph' && content?.length === 1 && content[0]!.type === 'link') {
          const target = resolve(b.path, content[0]!.href);
          if (target) {
            const page = ws.getPage(target);
            if (page?.kind === 'database') return { type: 'database', props: { dbId: target, viewSetId: target } };
            return page?.parent.type === 'page' && page.parent.id === b.id ? { type: 'page', props: { pageId: target } } : { type: 'linkToPage', props: { pageId: target } };
          }
        }
        // Links to imported pages inside running text become page mentions.
        const inline = content?.map((c): MdInline => {
          if (c.type !== 'link') return c;
          const target = resolve(b.path, c.href);
          return target ? { type: 'mention', props: { kind: 'page', id: target } } : c;
        });
        return { ...x, ...(inline ? { content: inline } : {}), ...(x.children ? { children: linkify(x.children) } : {}) };
      });
    target.setBody(b.id, linkify(blocks));
  }
  return roots;
}
